/**
 * DeleteInstanceDialog - Modal de confirmação para EXCLUIR definitivamente
 * uma instância (provider_instances) do tenant.
 *
 * ARQUITETURA:
 * - Diferente do CancelInstanceDialog (que cancela a assinatura no provedor de
 *   pagamento), aqui apenas o registro da instância é removido. O add-on/plano
 *   contratado permanece intacto e libera a cota para uma nova criação.
 * - A exclusão é executada pela RPC `delete_provider_instance` (SECURITY DEFINER),
 *   que valida ownership e bloqueia instâncias vinculadas a inbox.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-16 v1.0.0 - Versão inicial (layout premium espelhando o
 *                              CancelInstanceDialog).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Loader2, MessageCircle, Trash2 } from 'lucide-react';
import type { Instance } from '@/hooks/useInstances';

interface DeleteInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: Instance | null;
  /** Executa a exclusão (mutation do hook useInstances) */
  onConfirm: (instance: Instance) => Promise<unknown>;
}

export function DeleteInstanceDialog({
  open,
  onOpenChange,
  instance,
  onConfirm,
}: DeleteInstanceDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Confirma a exclusão definitiva da instância */
  const handleConfirm = async () => {
    if (!instance) return;

    setIsSubmitting(true);
    try {
      await onConfirm(instance);
      onOpenChange(false);
    } catch {
      // Toast de erro já é exibido pela mutation (useInstances)
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col">
        {/* ===== HEADER PREMIUM (tom destrutivo) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-destructive/5 via-destructive/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-destructive/10">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
              </div>
              Excluir instância?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              A instância será removida definitivamente. Sua assinatura/add-on
              continua ativo e você poderá criar uma nova instância em seguida.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Card de contexto */}
          <div className="rounded-lg border p-3 flex items-center gap-3 bg-muted/20">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">
                {instance.instance_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {instance.provider_display_name}
              </p>
            </div>
            <Badge
              variant="outline"
              className="font-mono text-[10px] text-muted-foreground border-muted-foreground/30"
            >
              {instance.id.substring(0, 8).toUpperCase()}
            </Badge>
          </div>

          {/* Aviso de ação definitiva */}
          <div className="rounded-lg border border-dashed p-4 text-sm text-foreground">
            Esta ação é <span className="font-semibold">definitiva</span>. Após
            excluir, use o botão{' '}
            <span className="font-semibold">"Nova Instância"</span> para criar
            outra — todas as regras de cota do seu plano continuam valendo.
          </div>
        </div>

        {/* ===== FOOTER PREMIUM ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Trash2 className="h-4 w-4 mr-1.5" />
            )}
            Excluir instância
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
